import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    InventoryAdjustmentCreate,
    InventoryAdjustmentDto,
    InventoryAdjustmentRead,
    InventoryAdjustmentSearchParams,
} from '../../entities/inventory-adjustment.dto';
import {
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.InventoryAdjustment);

withDefaultEntityMetadata<InventoryAdjustmentDto>(symbols.dto, {
    inventoryBundleId: String,
    adjustedTotalQuantity: Number,
    absoluteAdjustedTotalQuantity: Number,
    beforeTotalQuantity: Number,
    afterTotalQuantity: Number,
    adjustedReservedQuantity: Number,
    absoluteAdjustedReservedQuantity: Number,
    beforeReservedQuantity: Number,
    afterReservedQuantity: Number,
    adjustedAvailableQuantity: Number,
    absoluteAdjustedAvailableQuantity: Number,
    beforeAvailableQuantity: Number,
    afterAvailableQuantity: Number,
    adjustmentType: String,
    adjustmentReason: String,
    sourceEntity: String,
    sourceEntityId: String,
    exceptionId: String,
    notes: String,
    adjustedByUserId: String,
    adjustedByUser: Object,
    adjustmentPrice: Number,
    resourceAuth: ResourceAuthSymbol,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    inventoryBundle: Object,
});

withDefaultEntityMetadata<InventoryAdjustmentRead>(symbols.entity, {
    inventoryBundleId: String,
    adjustedTotalQuantity: Number,
    absoluteAdjustedTotalQuantity: Number,
    beforeTotalQuantity: Number,
    afterTotalQuantity: Number,
    adjustedReservedQuantity: Number,
    absoluteAdjustedReservedQuantity: Number,
    beforeReservedQuantity: Number,
    afterReservedQuantity: Number,
    adjustedAvailableQuantity: Number,
    absoluteAdjustedAvailableQuantity: Number,
    beforeAvailableQuantity: Number,
    afterAvailableQuantity: Number,
    adjustmentType: String,
    adjustmentReason: String,
    sourceEntity: String,
    sourceEntityId: String,
    exceptionId: String,
    notes: String,
    adjustedByUserId: String,
    adjustedByUser: Object,
    adjustmentPrice: Number,
    resourceAuth: ResourceAuthSymbol,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    inventoryBundle: Object,
});

withMetadata<InventoryAdjustmentCreate>(symbols.createBody, {
    inventoryBundleId: String,
    adjustedTotalQuantity: Number,
    absoluteAdjustedTotalQuantity: Number,
    beforeTotalQuantity: Number,
    afterTotalQuantity: Number,
    adjustedReservedQuantity: Number,
    absoluteAdjustedReservedQuantity: Number,
    beforeReservedQuantity: Number,
    afterReservedQuantity: Number,
    adjustedAvailableQuantity: Number,
    absoluteAdjustedAvailableQuantity: Number,
    beforeAvailableQuantity: Number,
    afterAvailableQuantity: Number,
    adjustmentType: String,
    adjustmentReason: String,
    sourceEntity: String,
    sourceEntityId: String,
    exceptionId: String,
    notes: String,
    adjustedByUserId: String,
    adjustmentPrice: Number,
    resourceAuth: ResourceAuthSymbol,
});

withDefaultSearchParamsMetadata<InventoryAdjustmentSearchParams>(
    symbols.searchParams,
    {
        itemId: String,
        containerId: String,
        slotId: String,
        inventoryBundleId: String,
        warehouseId: String,
    }
);

export const InventoryAdjustmentMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<InventoryAdjustmentRead, InventoryAdjustmentDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            )
        );
        createMap<InventoryAdjustmentDto, InventoryAdjustmentRead>(
            mapper,
            symbols.dto,
            symbols.entity,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            )
        );
        createMap<InventoryAdjustmentDto, InventoryAdjustmentCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            )
        );
        createMap<
            IQueryParams<InventoryAdjustmentDto>,
            InventoryAdjustmentSearchParams
        >(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withWarehouseSearchFilter(),
            withSearchFilterUnTyped('itemId'),
            withSearchFilterUnTyped('containerId'),
            withSearchFilterUnTyped('slotId'),
            withFragmentSearchFilter('inventoryBundle'),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        CustomFieldMetadataMap.profile(mapper);
    },
};
